/**
 * Report-step CRUD on the typed `@tiaki/http-client` surface (ADR 113
 * §Decision).
 *
 * `as never` casts on params/body bridge drf-spectacular's looser shapes.
 */
import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
  return result.data as T;
}

export type ReportStepFieldType = 'text' | 'single_select' | 'multi_select';

/**
 * ADR 143 §1 — the phase discriminator on a report-step template.
 *
 * `intake` questions are asked of the initiator when an alert/incident starts;
 * `end_event_survey` questions are asked of recipients once it ends.
 */
export type ReportStepPhase = 'intake' | 'end_event_survey';

export const REPORT_STEP_PHASES: readonly ReportStepPhase[] = [
  'intake',
  'end_event_survey',
] as const;

export const reportStepPhaseLabels: Record<ReportStepPhase, string> = {
  intake: 'Intake',
  end_event_survey: 'End-Event Survey',
};

export interface ReportStepOption {
  value: string;
  label: string;
}

/** ADR-144 D3 parameter declaration on intake phase report steps. */
export interface ReportStepParameter {
  announce?: boolean;
  announce_order?: number;
  required_at_create?: 'require' | 'prompt' | 'none';
}

export interface ReportStep {
  id?: number;
  alert_type: number;
  /**
   * ADR 143 §1. Required rather than optional on purpose: an optional
   * discriminator that a writer forgets to persist silently degrades to its
   * default. Making this required means a new write path cannot compile
   * without deciding the phase.
   */
  phase: ReportStepPhase;
  field_type: ReportStepFieldType;
  label: string;
  field_slug?: string;
  placeholder?: string;
  description?: string;
  options?: ReportStepOption[];
  order: number;
  is_required: boolean;
  allow_other?: boolean;
  parameter?: ReportStepParameter | null;
}

export interface ReportStepListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: ReportStep[];
}

/**
 * List the report steps of one alert type for one phase.
 *
 * `phase` is always sent explicitly. The server's list route is phase-scoped
 * and treats an ABSENT `phase` as `intake` (ADR 143 §3 fail-safe), so omitting
 * it would make the survey questions unreachable from this admin UI rather
 * than merely unfiltered.
 */
export async function getReportSteps(
  alertTypeId: number,
  phase: ReportStepPhase = 'intake',
): Promise<ReportStepListResponse> {
  const result = await client.GET('/api/v2/report-steps/', {
    params: { query: { alert_type: alertTypeId, phase } as never },
  });
  return unwrap(result) as unknown as ReportStepListResponse;
}

/**
 * The bulk routes take `{"steps": [...]}`, not a bare array — the server
 * reads `request.data.get("steps")` and 400s on a missing key.
 */
export async function bulkCreateReportSteps(
  steps: Omit<ReportStep, 'id'>[],
): Promise<ReportStep[]> {
  const result = await client.POST('/api/v2/report-steps/bulk-create/', {
    body: { steps } as never,
  });
  return unwrap(result) as unknown as ReportStep[];
}

export async function bulkUpdateReportSteps(
  steps: ReportStep[],
): Promise<ReportStep[]> {
  const result = await client.PUT('/api/v2/report-steps/bulk-update/', {
    body: { steps } as never,
  });
  return unwrap(result) as unknown as ReportStep[];
}

export async function deleteReportStep(id: number): Promise<void> {
  const result = await client.DELETE('/api/v2/report-steps/{id}/', {
    params: { path: { id } as never },
  });
  const __resp = result as { error?: unknown }; if (__resp.error !== undefined) throw __resp.error;
}
